/**
 * Over-the-air web bundles through `@capgo/capacitor-updater` in MANUAL mode
 * (plan D44, D45; repo D166 gives D06/D07 their mechanism).
 *
 * The rule is D07: an update applies on the next launch, never mid-workout.
 *
 * - A new bundle is downloaded in the background and queued with `next()`,
 *   behind a `kill` delay condition, so leaving the app for a music app
 *   between sets does not swap it.
 * - The NEXT launch applies it at boot, before the first render (see
 *   `shouldApplyPending`): the plugin itself would otherwise wait for the
 *   first background after that launch.
 * - `set()` and `autoUpdate: "atBackground"` are never used.
 *
 * Web: a no-op. A live-reload build (`server.url`, detected by origin): a no-op too, because it
 * loads from the VPS and a downloaded bundle would fight it.
 *
 * ── AND ONE DOOR THE PERSON OPENS ON PURPOSE ───────────────────────────────
 * "Procurar atualizações" in the profile runs the same check
 * ({@link checkForUpdateNow}) and, when a bundle is ready, offers to restart
 * into it ({@link restartIntoUpdate}). Nothing above changes for anyone who
 * never presses it: the restart is the person's own act, the one exception to
 * "next launch" — and the profile does not offer it while a workout runs.
 */
import { CapacitorHttp } from "@capacitor/core";
import { getAppInfo } from "../app";
import { isNative } from "../platform";
import { preferences } from "../storage";
import {
  type CheckOutcome,
  decideUpdate,
  type ManualUpdateCheck,
  manualResultOf,
  outcomeOfDecision,
  rolledBackVersion,
  shouldApplyPending,
} from "./decide";

export type { ManualUpdateCheck } from "./decide";

import { manifestUrl, otaManifest } from "./manifest";

/**
 * The version this app queued with `next()`. The plugin forgets a bundle it
 * rolled back (`autoDeleteFailed`), so without this record every launch would
 * download the same broken bundle again and wait 10 s for it to fail.
 */
const QUEUED_KEY = "ota.queued";

/**
 * How long the manifest request may take. It is given to `CapacitorHttp`,
 * never to the WebView's `fetch`.
 *
 * ── THE MANIFEST GOES THROUGH THE BRIDGE, AND MUST ─────────────────────────
 * The bundled shell's origin is `https://localhost` and `cdn.mypocketgym.app`
 * sends no `Access-Control-Allow-Origin` (its preflight answers 403), so a
 * cross-origin `fetch` for the manifest is refused by the browser before it
 * leaves — `TypeError: Failed to fetch`, one `[ota] check failed` line, and
 * no update, ever, on any installed app. `CapacitorHttp` makes the request
 * natively, outside the WebView, where CORS does not apply. Measured on the
 * container on 2026-09-21: `fetch` failed and `CapacitorHttp` answered 200
 * for the same URL in the same page. The bundle DOWNLOAD was never affected —
 * the Capgo plugin does that natively already.
 */
const MANIFEST_TIMEOUT_MS = 15_000;

type Updater = typeof import("@capgo/capacitor-updater")["CapacitorUpdater"];

/**
 * The bundled shell serves the app from exactly this origin
 * (`server.androidScheme: "https"`, default hostname). A `CAP_LIVE` build has
 * `server.url` set and loads from the dev server's origin instead.
 * `@capacitor/core` 8 exposes no public `getConfig()` to read `server.url`
 * directly, and the origin is the same fact seen from the page.
 */
const BUNDLED_ORIGIN = "https://localhost";

function isLiveReload(): boolean {
  return window.location.origin !== BUNDLED_ORIGIN;
}

/**
 * `CapacitorHttp`'s body, as something `otaManifest` can parse. The bridge
 * decodes a JSON content-type into an object and hands anything else back as
 * text, and a proxy or an error page is text.
 */
function manifestBody(data: unknown): unknown {
  if (typeof data !== "string") {
    return data;
  }
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

function log(message: string, detail?: unknown): void {
  // The only trace an update leaves (plan D44 applies it silently).
  //
  // ⚠️ The detail is SERIALISED rather than passed as a second argument.
  // `Capacitor/Console` flattens what it forwards to logcat, so an object
  // arrived as the literal `[object Object]` — and logcat is the only place
  // these lines are ever read, which made every detail this function exists to
  // carry unreadable exactly where it was needed (found on the owner's phone,
  // T123). `JSON.stringify` answers `undefined` for a bare `undefined` and
  // throws on a cycle, so both are handled rather than trusted.
  console.info(
    `[ota] ${message}`,
    detail === undefined ? "" : safeDetail(detail)
  );
}

function safeDetail(detail: unknown): string {
  if (typeof detail === "string") {
    return detail;
  }
  try {
    return JSON.stringify(detail) ?? String(detail);
  } catch {
    return String(detail);
  }
}

/**
 * Confirms the running bundle, applies one queued by an earlier launch, and
 * starts the check for a newer one.
 *
 * Resolves once `notifyAppReady()` has been sent and no queued bundle is
 * waiting, so a caller can await it before rendering without waiting on the
 * network: every step before the resolve is a local plugin call. When a
 * queued bundle is applied the page reloads and this never resolves, which is
 * what the caller wants — nothing renders twice. The background check never
 * rejects: offline, a bad manifest or a failed download end in one log line
 * and the app keeps the bundle it has.
 */
export async function startUpdater(): Promise<void> {
  if (!isNative() || isLiveReload()) {
    return;
  }
  const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
  // FIRST, unconditionally, before any network call. Not calling it in time is
  // what makes the plugin roll back, so a gym with no signal must never delay
  // it. "In time" is 30 s for a bundle applied at boot — the plugin's pending-
  // bundle floor, not `appReadyTimeout`'s 10 s (see `capacitor.config.ts`;
  // measured on a phone, T123) — so a broken bundle is a blank screen for
  // half a minute before the rollback, not ten seconds.
  await CapacitorUpdater.notifyAppReady();

  if (await applyQueuedBundle(CapacitorUpdater)) {
    return;
  }
  sharedCheck(CapacitorUpdater).catch((error: unknown) => {
    log("check failed", error);
  });
}

/**
 * The check in flight, shared: the boot's background check and a press of
 * "Procurar atualizações" during it are ONE check, so the bundle is never
 * downloaded twice and `next()` never raced.
 */
let inFlight: Promise<CheckOutcome> | null = null;

function sharedCheck(updater: Updater): Promise<CheckOutcome> {
  if (inFlight === null) {
    inFlight = checkForUpdate(updater).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Whether this build can install an update at all — the button's gate. */
export function updatesSupported(): boolean {
  return isNative() && !isLiveReload();
}

/**
 * The person asked: the same check the launch runs, awaited this time. A
 * bundle it downloads is queued exactly as the background one would be, so
 * NOT restarting leaves the ordinary next-launch path intact.
 */
export async function checkForUpdateNow(): Promise<ManualUpdateCheck> {
  if (!updatesSupported()) {
    return { kind: "unsupported" };
  }
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
    return manualResultOf(await sharedCheck(CapacitorUpdater));
  } catch (error: unknown) {
    log("manual check failed", error);
    return { kind: "failed" };
  }
}

/**
 * Restart into the queued bundle, now. `reload()` applies the bundle `next()`
 * queued whatever its delay condition (the plugin's Android `reload`, 8.51),
 * which is why the `kill` condition is left in place: if the reload fails,
 * the bundle still waits for the next launch rather than for the first
 * background. Never resolves when it works — the page is replaced.
 */
export async function restartIntoUpdate(): Promise<void> {
  if (!updatesSupported()) {
    return;
  }
  const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
  log("restarting into the queued bundle on request");
  await CapacitorUpdater.reload();
}

/** True when the page is being replaced by the queued bundle. */
async function applyQueuedBundle(updater: Updater): Promise<boolean> {
  try {
    const [pending, current] = await Promise.all([
      updater.getNextBundle(),
      updater.current(),
    ]);
    if (!shouldApplyPending({ currentId: current.bundle.id, pending })) {
      return false;
    }
    log("applying queued bundle", pending?.version);
    await updater.reload();
    return true;
  } catch (error: unknown) {
    // Keep the old bundle for this session, and hold the queued one back
    // again: the `kill` condition was cleared at launch, and without it the
    // plugin would swap at the first background.
    log("apply failed", error);
    await updater
      .setMultiDelay({ delayConditions: [{ kind: "kill" }] })
      .catch(() => undefined);
    return false;
  }
}

async function checkForUpdate(updater: Updater): Promise<CheckOutcome> {
  const currentVersion = import.meta.env.VITE_BUNDLE_VERSION;

  const [pending, queued] = await Promise.all([
    updater.getNextBundle(),
    preferences.get(QUEUED_KEY),
  ]);
  const pendingVersion = pending?.version ?? null;
  if (queued === currentVersion) {
    await preferences.remove(QUEUED_KEY);
  }
  const failedVersion = rolledBackVersion({
    currentVersion,
    pendingVersion,
    queued,
  });

  const response = await CapacitorHttp.get({
    connectTimeout: MANIFEST_TIMEOUT_MS,
    headers: { "Cache-Control": "no-cache" },
    readTimeout: MANIFEST_TIMEOUT_MS,
    url: manifestUrl(import.meta.env.VITE_OTA_ENV),
  });
  if (response.status !== 200) {
    log(`manifest ${response.status}`);
    return { kind: "unreadable" };
  }
  const parsed = otaManifest.safeParse(manifestBody(response.data));
  if (!parsed.success) {
    log("manifest rejected", parsed.error.issues);
    return { kind: "unreadable" };
  }
  const manifest = parsed.data;

  const info = await getAppInfo();
  const nativeBuild = Number.parseInt(info?.build ?? "", 10);
  const decision = decideUpdate({
    currentVersion,
    failedVersion,
    manifest,
    nativeBuild,
    pendingVersion,
  });
  if (decision !== "download") {
    log(decision, { current: currentVersion, manifest: manifest.version });
    return outcomeOfDecision(decision, manifest.version);
  }

  const bundle = await updater.download({
    checksum: manifest.checksum,
    sessionKey: manifest.sessionKey,
    url: manifest.url,
    version: manifest.version,
  });
  // The delay goes on BEFORE `next()`: set after it, a background between the
  // two calls would swap the bundle mid-session.
  await updater.setMultiDelay({ delayConditions: [{ kind: "kill" }] });
  // Recorded before `next()`: if the bundle is later rolled back, the next
  // launch reads this and never downloads it again.
  await preferences.set(QUEUED_KEY, manifest.version);
  await updater.next({ id: bundle.id });
  log("queued for next launch", { from: currentVersion, to: bundle.version });
  return { kind: "queued", version: bundle.version };
}
